<script setup>
/**
 * The case type's own fields, and the values this case holds for them.
 *
 * The shapes come from `field_definitions` and the values from `custom_fields`
 * keyed by the definition's `key` (api.md) — the panel never invents a field, so
 * a type that defines none renders as an honest empty section rather than a
 * blank box.
 *
 * One field saves at a time, because the write is a merge: emitting
 * `{ key, value }` is exactly what the endpoint takes, and `null` is how a value
 * is cleared rather than how it is omitted.
 */
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import CaseFieldInput from './CaseFieldInput.vue';

const props = defineProps({
  // `{ id, key, label, field_type, options, required, position }`, camelised.
  fieldDefinitions: {
    type: Array,
    default: () => [],
  },
  // The case's `customFields` object; a key with no entry is an unset field.
  values: {
    type: Object,
    default: () => ({}),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update']);

const { t } = useI18n();

// The editable copy the inputs bind to. Kept in step with the record so a
// mutation elsewhere (or a rejected write rolled back by the screen) shows here
// instead of leaving a stale draft on screen.
const drafts = ref({});

const syncDrafts = () => {
  drafts.value = props.fieldDefinitions.reduce((draft, definition) => {
    draft[definition.key] = props.values?.[definition.key] ?? null;
    return draft;
  }, {});
};

watch(() => [props.fieldDefinitions, props.values], syncDrafts, {
  immediate: true,
  deep: true,
});

const fields = computed(() =>
  [...props.fieldDefinitions].sort((a, b) => a.position - b.position)
);

// An empty text box means "no value", which is a value the merge has to carry:
// `null` clears the field, `''` would store an empty string.
const normalize = value => {
  if (value === '' || value === undefined) return null;
  return value;
};

const commit = (definition, value) => {
  const next = normalize(value);
  if (next === (props.values?.[definition.key] ?? null)) return;
  emit('update', { key: definition.key, value: next });
};
</script>

<template>
  <section class="flex flex-col gap-3" data-testid="case-fields-panel">
    <h3 class="mb-0 text-sm font-medium text-n-slate-12">
      {{ t('SUPPORT_CASES.DETAIL.FIELDS.TITLE') }}
    </h3>

    <p
      v-if="!fields.length"
      class="text-sm text-n-slate-11"
      data-testid="case-fields-empty"
    >
      {{ t('SUPPORT_CASES.DETAIL.FIELDS.EMPTY') }}
    </p>

    <div v-else class="flex flex-col gap-3">
      <div
        v-for="definition in fields"
        :key="definition.id"
        class="flex flex-col gap-1"
        :data-testid="`case-field-${definition.key}`"
      >
        <label
          :for="`case-field-input-${definition.id}`"
          class="text-xs text-n-slate-11"
        >
          {{ definition.label }}
          <span v-if="definition.required" class="text-n-ruby-11">*</span>
        </label>

        <CaseFieldInput
          :definition="definition"
          :model-value="drafts[definition.key]"
          :disabled="disabled"
          @update:model-value="drafts[definition.key] = $event"
          @commit="commit(definition, $event)"
        />
      </div>
    </div>
  </section>
</template>
